import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const PAGE_SELECTOR = '.esg-report-page';

/**
 * Rasterizes each `.esg-report-page` under `root` onto sequential A4 pages and triggers download.
 * Frontend-only; expects same-origin assets so canvas is not tainted.
 */
export async function downloadReportPdf(root: HTMLElement, fileName: string): Promise<void> {
  const pages = root.querySelectorAll(PAGE_SELECTOR);
  if (pages.length === 0) {
    throw new Error('No report pages found for PDF export.');
  }

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidthMm = pdf.internal.pageSize.getWidth();
  const pageHeightMm = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < pages.length; i++) {
    const el = pages[i] as HTMLElement;
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png', 1.0);
    const srcWidth = canvas.width;
    const srcHeight = canvas.height;

    const ratio = pageWidthMm / srcWidth;
    const imgHeightMm = srcHeight * ratio;

    if (i > 0) {
      pdf.addPage();
    }

    let renderHeightMm = imgHeightMm;
    let renderWidthMm = pageWidthMm;

    if (imgHeightMm > pageHeightMm) {
      renderHeightMm = pageHeightMm;
      renderWidthMm = (srcWidth * pageHeightMm) / srcHeight;
    }

    const offsetXMm = (pageWidthMm - renderWidthMm) / 2;
    const offsetYMm = 0;

    pdf.addImage(imgData, 'PNG', offsetXMm, offsetYMm, renderWidthMm, renderHeightMm);
  }

  pdf.save(fileName);
}

export function sanitizeReportFileBaseName(raw: string): string {
  return raw
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64) || 'report';
}
